import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Search, X, Crown, ShieldCheck, User, Sparkles, Ban, Clock, DollarSign, TrendingUp, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { createPortal } from "react-dom";

const TEAL = "#0D9488";
const PLAN_META = {
  free:  { label: "Free",  icon: User,        chipCls: "bg-slate-100 text-slate-600 border-slate-200" },
  pro:   { label: "Pro",   icon: Crown,       chipCls: "bg-amber-100 text-amber-800 border-amber-200" },
  coach: { label: "Coach", icon: ShieldCheck, chipCls: "bg-teal-100 text-teal-800 border-teal-200" },
};

function relative(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  const diff = Date.now() - d.getTime();
  const past = diff >= 0;
  const abs = Math.abs(diff);
  const s = Math.floor(abs / 1000);
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  const days = Math.floor(h / 24);
  const label = s < 60 ? `${s}s` : m < 60 ? `${m}m` : h < 24 ? `${h}h` : days < 30 ? `${days}d` : d.toISOString().slice(0, 10);
  return past ? `${label} ago` : `in ${label}`;
}

export default function AdminMembershipsCard() {
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [planFilter, setPlanFilter] = useState(null); // null | free | pro | coach
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ rows: [], counts: null, total: 0, has_more: false });
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [grantTarget, setGrantTarget] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedQ(q); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(() => {
    setLoading(true);
    const params = { q: debouncedQ, page, page_size: 20 };
    if (planFilter) params.plan = planFilter;
    api.get("/admin/memberships", { params })
      .then(({ data }) => setData({
        rows: data.rows || [],
        counts: data.counts || null,
        total: data.total || 0,
        has_more: !!data.has_more,
      }))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [debouncedQ, page, planFilter]);
  useEffect(() => { load(); }, [load]);

  const loadEvents = useCallback(() => {
    setEventsLoading(true);
    api.get("/admin/memberships/events", { params: { limit: 30 } })
      .then(({ data }) => setEvents(Array.isArray(data) ? data : []))
      .catch(() => setEvents([]))
      .finally(() => setEventsLoading(false));
  }, []);
  useEffect(() => { loadEvents(); }, [loadEvents]);

  const pageMax = Math.max(1, Math.ceil(data.total / 20));

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="admin-memberships-card">
      <div className="flex items-center gap-2 mb-1">
        <Crown className="w-4 h-4" style={{ color: TEAL }} />
        <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: TEAL }}>Membership management</div>
      </div>
      <h3 className="font-display font-bold text-xl text-slate-900 mb-1">Plans & revenue</h3>
      <p className="text-sm text-slate-500 mb-4">
        Manually grant Pro access, revoke it, and track a rolling log of every grant, revoke, and payment.
      </p>

      {/* Counts summary chips */}
      {data.counts && (
        <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="admin-memberships-summary">
          <SummaryChip label="Total" value={data.total} onClick={() => setPlanFilter(null)} active={planFilter === null} icon={TrendingUp} />
          <SummaryChip label="Pro" value={data.counts.pro} onClick={() => setPlanFilter(planFilter === "pro" ? null : "pro")} active={planFilter === "pro"} icon={Crown} accent="amber" />
          <SummaryChip label="Coach" value={data.counts.coach} onClick={() => setPlanFilter(planFilter === "coach" ? null : "coach")} active={planFilter === "coach"} icon={ShieldCheck} accent="teal" />
          <SummaryChip label="Free" value={data.counts.free} onClick={() => setPlanFilter(planFilter === "free" ? null : "free")} active={planFilter === "free"} icon={User} accent="slate" />
        </div>
      )}

      {/* Search */}
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="Search users…"
               className="pl-9 bg-slate-50 border-slate-200 focus-visible:ring-0 focus-visible:border-[#0D9488]"
               data-testid="admin-memberships-search" />
        {q && (
          <button type="button" onClick={() => setQ("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  data-testid="admin-memberships-search-clear" aria-label="Clear"><X className="w-3.5 h-3.5" /></button>
        )}
      </div>

      {loading ? (
        <div className="space-y-1.5">
          {[0,1,2,3,4].map((i) => <div key={i} className="h-14 bg-slate-50 border border-slate-100 rounded animate-pulse" />)}
        </div>
      ) : data.rows.length === 0 ? (
        <div className="text-xs text-slate-500 italic border border-dashed border-slate-200 rounded-md p-6 text-center"
             data-testid="admin-memberships-empty">
          {debouncedQ || planFilter ? "No users match this filter." : "No users yet."}
        </div>
      ) : (
        <ul className="space-y-1.5" data-testid="admin-memberships-list">
          {data.rows.map((u) => {
            const meta = PLAN_META[u.plan] || PLAN_META.free;
            const Icon = meta.icon;
            return (
              <li key={u.id} className="border border-slate-100 rounded-lg p-3 flex items-center gap-3 hover:bg-slate-50 transition-colors"
                  data-testid={`admin-memberships-row-${u.id}`}>
                <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-[10px] text-white shrink-0"
                     style={{ background: "linear-gradient(135deg,#94A3B8 0%,#475569 100%)" }}>
                  {(u.handle || u.name || u.email || "?").slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-semibold text-slate-900 truncate">
                      {u.name || `@${u.handle}`}
                    </div>
                    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-widest font-semibold border ${meta.chipCls}`}
                          data-testid={`admin-memberships-plan-${u.id}`}>
                      <Icon className="w-2.5 h-2.5" /> {meta.label}
                    </span>
                    {u.banned && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-widest font-semibold bg-rose-100 text-rose-700 border border-rose-200">
                        <Ban className="w-2.5 h-2.5" /> Banned
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 font-mono-num truncate">{u.email}</div>
                  {(u.premium_granted_at || u.premium_expires_at) && u.has_premium && (
                    <div className="text-[10px] text-amber-700 mt-0.5 flex items-center gap-2 flex-wrap"
                         data-testid={`admin-memberships-grant-meta-${u.id}`}>
                      {u.premium_granted_at && (
                        <span className="inline-flex items-center gap-1"><Sparkles className="w-2.5 h-2.5" />
                          Granted {relative(u.premium_granted_at)}{u.premium_granted_by ? ` · by ${u.premium_granted_by}` : ""}
                        </span>
                      )}
                      {u.premium_expires_at && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 text-[10px] font-semibold"
                              data-testid={`admin-memberships-expiry-${u.id}`}
                              title={`Auto-reverts to Free on ${new Date(u.premium_expires_at).toLocaleString()}`}>
                          <Clock className="w-2.5 h-2.5" /> Auto-expires {relative(u.premium_expires_at)}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="shrink-0">
                  {u.has_premium ? (
                    <Button type="button" size="sm" variant="ghost"
                            onClick={() => setRevokeTarget(u)}
                            className="text-rose-600 hover:bg-rose-50 h-8 text-[11px]"
                            data-testid={`admin-memberships-revoke-${u.id}`}>
                      Revoke Pro
                    </Button>
                  ) : (
                    <Button type="button" size="sm"
                            onClick={() => setGrantTarget(u)}
                            className="text-white h-8 text-[11px]"
                            style={{ background: TEAL }}
                            data-testid={`admin-memberships-grant-${u.id}`}>
                      <Crown className="w-3 h-3 mr-1" /> Grant Pro
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Pagination */}
      {data.total > 20 && (
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
          <div className="text-[11px] text-slate-500 font-mono-num">
            Page {page} of {pageMax} · {data.total.toLocaleString()} users
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" size="sm" variant="outline"
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    data-testid="admin-memberships-prev">Prev</Button>
            <Button type="button" size="sm" variant="outline"
                    disabled={!data.has_more || loading}
                    onClick={() => setPage((p) => p + 1)}
                    data-testid="admin-memberships-next">Next</Button>
          </div>
        </div>
      )}

      {/* Events log */}
      <div className="mt-6 pt-4 border-t border-slate-100" data-testid="admin-memberships-events">
        <RevenueSparkline />
        <div className="flex items-center gap-2 mb-2 mt-4">
          <Clock className="w-3.5 h-3.5 text-slate-500" />
          <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Recent revenue & plan events</div>
        </div>
        {eventsLoading ? (
          <div className="h-24 bg-slate-50 rounded animate-pulse" />
        ) : events.length === 0 ? (
          <div className="text-xs text-slate-500 italic border border-dashed border-slate-200 rounded-md p-4 text-center">
            No plan events yet. Every grant, revoke, or paid subscription lands here for revenue tracking.
          </div>
        ) : (
          <ul className="space-y-1 max-h-72 overflow-auto" data-testid="admin-memberships-events-list">
            {events.map((e) => <EventRow key={e.id} e={e} />)}
          </ul>
        )}
      </div>

      {grantTarget && (
        <GrantDialog user={grantTarget}
                     onClose={() => setGrantTarget(null)}
                     onGranted={() => { setGrantTarget(null); load(); loadEvents(); }} />
      )}
      {revokeTarget && (
        <RevokeDialog user={revokeTarget}
                      onClose={() => setRevokeTarget(null)}
                      onRevoked={() => { setRevokeTarget(null); load(); loadEvents(); }} />
      )}
    </div>
  );
}

function SummaryChip({ label, value, onClick, active, icon: Icon, accent = "teal" }) {
  const activeCls = accent === "amber" ? "bg-amber-100 text-amber-800 border-amber-200"
                  : accent === "slate" ? "bg-slate-200 text-slate-800 border-slate-300"
                  : "bg-teal-100 text-teal-800 border-teal-200";
  const idleCls = "bg-white text-slate-600 border-slate-200 hover:bg-slate-50";
  return (
    <button type="button" onClick={onClick}
            className={`inline-flex items-center gap-1.5 px-2.5 h-8 rounded-full border text-[11px] font-semibold transition-colors ${active ? activeCls : idleCls}`}
            data-testid={`admin-memberships-chip-${label.toLowerCase()}`}>
      <Icon className="w-3 h-3" />
      {label}
      <span className="tabular-nums opacity-90">{value.toLocaleString()}</span>
    </button>
  );
}

function EventRow({ e }) {
  const EVENT_META = {
    grant:   { icon: Sparkles,   color: "text-teal-700",  label: "Granted Pro" },
    revoke:  { icon: Ban,        color: "text-rose-600",  label: "Revoked Pro" },
    payment: { icon: DollarSign, color: "text-emerald-700", label: "Payment received" },
  };
  const meta = EVENT_META[e.event] || EVENT_META.grant;
  const Icon = meta.icon;
  return (
    <li className="flex items-start gap-2 text-[13px] py-1.5 border-b border-slate-100 last:border-0"
        data-testid={`admin-memberships-event-${e.id}`}>
      <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${meta.color}`} />
      <div className="flex-1 min-w-0">
        <div className="text-slate-800">
          <b>{meta.label}</b> · <span className="font-mono-num text-slate-600">{e.user_email}</span>
          {e.amount && (
            <span className="text-emerald-700 font-semibold ml-1">
              {e.currency === "USD" ? "$" : ""}{e.amount} {e.provider ? `(${e.provider})` : ""}
            </span>
          )}
        </div>
        {e.reason && <div className="text-[11px] text-slate-500 italic">"{e.reason}"</div>}
        <div className="text-[10px] text-slate-400 font-mono-num">
          {relative(e.at)}{e.actor_email ? ` · by ${e.actor_email}` : ""}
        </div>
      </div>
    </li>
  );
}

function RevenueSparkline() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/memberships/revenue-30d")
      .then(({ data }) => setData(data))
      .catch(() => setData({ series: [], total_30d: 0, count_30d: 0, providers: [] }));
  }, []);
  if (!data) return <div className="h-16 bg-slate-50 rounded animate-pulse" data-testid="admin-memberships-sparkline-loading" />;
  const providers = data.providers || [];
  const PROVIDER_COLORS = { paypal: "#0070ba", stripe: "#635BFF", venmo: "#008CFF", manual: "#0D9488", unknown: "#94A3B8" };
  const colorOf = (p) => PROVIDER_COLORS[p] || PROVIDER_COLORS.unknown;
  const max = Math.max(1, ...data.series.map((d) => d.amount));
  const W = 320, H = 60, PAD = 3;
  const step = data.series.length > 1 ? (W - PAD * 2) / (data.series.length - 1) : 0;
  // One polyline per provider so admins see each channel independently.
  const providerLines = providers.map((prov) => {
    const pts = data.series.map((d, i) => {
      const x = PAD + i * step;
      const amt = d.by_provider?.[prov] || 0;
      const y = H - PAD - ((amt / max) * (H - PAD * 2));
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(" ");
    return { prov, points: pts };
  });
  return (
    <div className="bg-gradient-to-br from-teal-50 to-emerald-50/40 border border-teal-100 rounded-lg p-4 flex items-start gap-4"
         data-testid="admin-memberships-sparkline">
      <div className="shrink-0">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-teal-700">30-day revenue</div>
        <div className="font-display font-bold text-2xl text-slate-900 tabular-nums" data-testid="admin-memberships-sparkline-total">
          ${data.total_30d.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
        <div className="text-[11px] text-slate-500 font-mono-num mb-2">{data.count_30d} payment{data.count_30d === 1 ? "" : "s"}</div>
        {providers.length > 0 && (
          <ul className="space-y-0.5" data-testid="admin-memberships-sparkline-legend">
            {providers.map((prov) => {
              const total = data.series.reduce((sum, d) => sum + (d.by_provider?.[prov] || 0), 0);
              return (
                <li key={prov} className="flex items-center gap-1.5 text-[11px]">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: colorOf(prov) }} />
                  <span className="capitalize text-slate-700">{prov}</span>
                  <span className="text-slate-400 tabular-nums ml-auto pl-2">${total.toFixed(2)}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <div className="flex-1 min-w-0">
        {data.series.length === 0 || data.count_30d === 0 ? (
          <div className="text-[11px] text-slate-500 italic text-center py-4">
            No payments in the last 30 days yet. Real revenue lands here once the payment webhook is wired to your provider.
          </div>
        ) : (
          <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-16" preserveAspectRatio="none">
            {providerLines.map(({ prov, points }) => (
              <polyline key={prov} points={points} fill="none" stroke={colorOf(prov)} strokeWidth="1.6"
                        strokeLinejoin="round" strokeLinecap="round" opacity="0.9"
                        data-testid={`admin-memberships-sparkline-line-${prov}`} />
            ))}
          </svg>
        )}
      </div>
    </div>
  );
}


function GrantDialog({ user, onClose, onGranted }) {
  const [reason, setReason] = useState("");
  const [expiresAt, setExpiresAt] = useState(""); // datetime-local
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { reason: reason.trim() || null };
      if (expiresAt) {
        const iso = new Date(expiresAt).toISOString();
        payload.expires_at = iso;
      }
      await api.post(`/admin/memberships/${user.id}/grant`, payload);
      toast.success(expiresAt
        ? `Granted Pro to ${user.name || user.email} until ${new Date(expiresAt).toLocaleString()}`
        : `Granted Pro to ${user.name || user.email}`);
      onGranted();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const nowLocal = () => {
    const d = new Date(Date.now() + 60_000);
    const off = d.getTimezoneOffset();
    return new Date(d.getTime() - off * 60_000).toISOString().slice(0, 16);
  };

  return createPortal(
    <div className="fixed inset-0 z-[9990] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="admin-memberships-grant-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
           onClick={(e) => e.stopPropagation()} data-testid="admin-memberships-grant-dialog">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest font-semibold text-[#0D9488]">Grant Pro access</div>
            <h3 className="font-display font-bold text-lg text-slate-900 mt-0.5">{user.name || user.email}</h3>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"
                  aria-label="Close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-slate-600">
            Flips the user's Pro flag on immediately, unlocking every premium playbook and gated feature. Logged in the events feed for revenue tracking.
          </p>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">Reason (optional · logged in events feed)</label>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value.slice(0, 500))}
                      placeholder="e.g. Comped for beta feedback / annual paid via Stripe checkout"
                      className="bg-white border-slate-200 focus-visible:ring-0 focus-visible:border-[#0D9488] text-sm"
                      data-testid="admin-memberships-grant-reason" />
            <div className="text-[10px] text-slate-400 font-mono-num mt-1">{reason.length}/500</div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block flex items-center gap-1">
              <Clock className="w-3 h-3" /> Expires on (optional · auto-revokes at this time)
            </label>
            <Input
              type="datetime-local"
              value={expiresAt}
              min={nowLocal()}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="bg-white border-slate-200 focus-visible:ring-0 focus-visible:border-[#0D9488] h-9 text-sm"
              data-testid="admin-memberships-grant-expires" />
            {expiresAt && (
              <div className="text-[10px] text-teal-700 font-mono-num mt-1">
                Auto-reverts to Free on {new Date(expiresAt).toLocaleString()}
              </div>
            )}
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/60 flex items-center justify-end gap-2">
          <Button type="button" size="sm" variant="ghost" onClick={onClose}
                  className="text-slate-700 hover:bg-slate-100"
                  data-testid="admin-memberships-grant-cancel">Cancel</Button>
          <Button type="button" size="sm" onClick={submit} disabled={busy}
                  className="text-white" style={{ background: TEAL }}
                  data-testid="admin-memberships-grant-confirm">
            <Crown className="w-3.5 h-3.5 mr-1" /> {busy ? "Granting…" : "Grant Pro"}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function RevokeDialog({ user, onClose, onRevoked }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/memberships/${user.id}/revoke`, { reason: reason.trim() || null });
      toast.success(`Revoked Pro from ${user.name || user.email}`);
      onRevoked();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9990] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="admin-memberships-revoke-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
           onClick={(e) => e.stopPropagation()} data-testid="admin-memberships-revoke-dialog">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest font-semibold text-rose-600">Revoke Pro access</div>
            <h3 className="font-display font-bold text-lg text-slate-900 mt-0.5">{user.name || user.email}</h3>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"
                  aria-label="Close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-slate-600">
            The user immediately loses access to premium playbooks + gated features. Their data is untouched — flip Pro back on anytime.
          </p>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">Reason (optional · logged)</label>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value.slice(0, 500))}
                      placeholder="e.g. Refund issued / subscription lapsed / test cleanup"
                      className="bg-white border-slate-200 focus-visible:ring-0 focus-visible:border-[#0D9488] text-sm"
                      data-testid="admin-memberships-revoke-reason" />
            <div className="text-[10px] text-slate-400 font-mono-num mt-1">{reason.length}/500</div>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/60 flex items-center justify-end gap-2">
          <Button type="button" size="sm" variant="ghost" onClick={onClose}
                  className="text-slate-700 hover:bg-slate-100"
                  data-testid="admin-memberships-revoke-cancel">Cancel</Button>
          <Button type="button" size="sm" onClick={submit} disabled={busy}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  data-testid="admin-memberships-revoke-confirm">
            <Ban className="w-3.5 h-3.5 mr-1" /> {busy ? "Revoking…" : "Revoke Pro"}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
